/**
 * Notifications store.
 *
 * Fed by the shared push demux (`useNotificationStream`) from live
 * `notification.event` + replayed `notification.events` frames — on web over the
 * cloud bridge, on desktop over the daemon's `/events` relay
 * (events ADR-012), and NEVER by REST polling. A notification `kind`
 * (info/success) carries NO severity.
 */

import { atom } from 'jotai';
import type { CredentialUnreadableCause } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';
import { type NotificationEntryKind, type NotificationMutePrefs } from './mute-prefs';

export {
  notificationMutePrefsAtom,
  type NotificationEntryKind,
  type NotificationKind,
  type NotificationMutePrefs,
} from './mute-prefs';

export interface NotificationEntry {
  id: string;
  eventId: string;
  kind: NotificationEntryKind;
  title: string;
  body: string;
  serviceOrigin: string;
  sessionId?: string;
  workspaceId?: string;
  /** Optional structured payload surfaced by the details expander. */
  details?: Record<string, unknown>;
  timestamp: string;
  read: boolean;
}

export const UNREADABLE_CREDENTIAL_ORIGIN = 'shogunchop-desktop';

/** The user-visible half of a discarded-credential event: a discarded saved sign-in explains itself and
 *  directs the user to sign in again (sdk ADR-014). */
export function unreadableCredentialNotification(
  cause: CredentialUnreadableCause,
): NotificationEntry {
  return {
    id: `credential-unreadable-${cause}`,
    eventId: `credential-unreadable-${cause}`,
    kind: 'warning',
    title: "Your saved sign-in couldn't be read",
    body: 'It was discarded, so please sign in again.',
    serviceOrigin: UNREADABLE_CREDENTIAL_ORIGIN,
    details: { cause },
    timestamp: new Date().toISOString(),
    read: false,
  };
}

export const notificationEntriesAtom = configureAtomDevtools(
  atom<NotificationEntry[]>([]),
  'notifications/entries',
);

export function isNotificationMuted(
  entry: Pick<NotificationEntry, 'kind' | 'serviceOrigin'>,
  prefs: NotificationMutePrefs,
): boolean {
  return (
    prefs.mutedKinds.some((kind) => kind === entry.kind) ||
    prefs.mutedOrigins.includes(entry.serviceOrigin)
  );
}

export const addNotificationEntryAction = configureAtomDevtools(
  atom<null, [NotificationEntry], void>(null, (get, set, entry: NotificationEntry) => {
    const entries = get(notificationEntriesAtom);
    if (entries.some((e) => e.eventId === entry.eventId)) return;
    set(notificationEntriesAtom, [entry, ...entries].slice(0, 100));
  }),
  'notifications/addEntry',
);
